import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, Checkbox, Typogaphy } from 'vnetwork-ui'
import {
    Download,
    ImageDown,
    TrendingUp,
    TrendingDown,
    Minus,
    Lightbulb,
} from 'lucide-react'
import html2canvas from 'html2canvas'
import * as XLSX from 'xlsx'
import { ProductGroupReportItem } from '../types'
import { SCHEMES } from '@/components/EchartCustomized'

interface ProductGroupReportProps {
    data: ProductGroupReportItem[]
    previousYearData?: ProductGroupReportItem[]
    showComparison: boolean
    year: number
    fromDate: string
    toDate: string
}

const COLORS = SCHEMES.default

const ProductGroupReport: React.FC<ProductGroupReportProps> = ({
    data,
    previousYearData,
    showComparison,
    year,
    fromDate,
    toDate,
}) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)
    const breakdownChartRef = useRef<HTMLDivElement>(null)
    const tableRef = useRef<HTMLDivElement>(null)

    // All chart data with colors assigned (before filtering)
    const allChartData = useMemo(() => {
        return data
            .sort((a, b) => b.total_services - a.total_services)
            .map((item, index) => {
                const prevItem = previousYearData?.find(
                    (p) => p.product_group_name === item.product_group_name
                )
                // Retained = total - new (existing services that are still active)
                const retained_services = Math.max(
                    0,
                    item.total_services - item.new_services
                )
                return {
                    ...item,
                    color: COLORS[index % COLORS.length],
                    shortName:
                        item.product_group_name.length > 12
                            ? item.product_group_name.substring(0, 12) + '...'
                            : item.product_group_name,
                    retained_services,
                    prev_total_services: prevItem?.total_services || 0,
                    prev_new_services: prevItem?.new_services || 0,
                    prev_cancelled_services: prevItem?.cancelled_services || 0,
                    prev_retention_rate: prevItem?.retention_rate || 0,
                }
            })
    }, [data, previousYearData])

    // Selection state - default all selected
    const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set())

    // Initialize selection when data changes
    useEffect(() => {
        if (allChartData.length > 0) {
            setSelectedGroups(
                new Set(allChartData.map((d) => d.product_group_name))
            )
        }
    }, [allChartData.length])

    // Check if all are selected
    const allSelected = selectedGroups.size === allChartData.length

    // Toggle single group
    const toggleGroup = (groupName: string) => {
        setSelectedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(groupName)) {
                next.delete(groupName)
            } else {
                next.add(groupName)
            }
            return next
        })
    }

    // Toggle all
    const toggleAll = () => {
        if (allSelected) {
            setSelectedGroups(new Set())
        } else {
            setSelectedGroups(
                new Set(allChartData.map((d) => d.product_group_name))
            )
        }
    }

    // Filtered chart data based on selection
    const chartData = useMemo(() => {
        return allChartData.filter((item) =>
            selectedGroups.has(item.product_group_name)
        )
    }, [allChartData, selectedGroups])

    const totalServices = useMemo(() => {
        return data.reduce((sum, d) => sum + d.total_services, 0)
    }, [data])

    const totalLostRevenue = useMemo(() => {
        return data.reduce((sum, d) => sum + d.lost_revenue, 0)
    }, [data])

    const avgRetentionRate = useMemo(() => {
        if (data.length === 0) return 0
        return data.reduce((sum, d) => sum + d.retention_rate, 0) / data.length
    }, [data])

    // Analysis data
    const analysis = useMemo(() => {
        if (data.length < 2) return null

        // Sort by different metrics
        const sortedByTotal = [...data].sort(
            (a, b) => b.total_services - a.total_services
        )
        const sortedByGrowth = [...data].sort(
            (a, b) => b.growth_rate - a.growth_rate
        )
        const sortedByRetention = [...data].sort(
            (a, b) => b.retention_rate - a.retention_rate
        )
        const sortedByCancelled = [...data].sort(
            (a, b) => b.cancelled_services - a.cancelled_services
        )

        const topProduct = sortedByTotal[0]
        const fastestGrowing = sortedByGrowth[0]
        const bestRetention = sortedByRetention[0]
        const mostCancelled = sortedByCancelled[0]

        // Calculate total new and cancelled
        const totalNew = data.reduce((sum, d) => sum + d.new_services, 0)
        const totalCancelled = data.reduce(
            (sum, d) => sum + d.cancelled_services,
            0
        )

        return {
            topProduct,
            fastestGrowing,
            bestRetention,
            mostCancelled,
            totalNew,
            totalCancelled,
        }
    }, [data])

    const exportToImage = async () => {
        if (!chartRef.current) return
        const canvas = await html2canvas(chartRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
        })
        const link = document.createElement('a')
        link.download = `product_group_chart_${fromDate}_${toDate}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }

    const exportToExcel = () => {
        const exportData = data.map((item) => ({
            [t('product_group')]: item.product_group_name,
            [t('total_services')]: item.total_services,
            [t('new_services')]: item.new_services,
            [t('cancelled_services')]: item.cancelled_services,
            [t('lost_revenue')]: item.lost_revenue,
            [t('retention_rate')]: `${item.retention_rate.toFixed(1)}%`,
            [t('growth_rate')]:
                `${item.growth_rate >= 0 ? '+' : ''}${item.growth_rate.toFixed(1)}%`,
        }))

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(exportData)
        XLSX.utils.book_append_sheet(wb, ws, t('product_group_report'))
        XLSX.writeFile(wb, `product_group_report_${fromDate}_${toDate}.xlsx`)
    }

    const formatCurrency = (amount: number) => {
        if (amount >= 1000000000) {
            return `${(amount / 1000000000).toFixed(1)}B`
        }
        if (amount >= 1000000) {
            return `${(amount / 1000000).toFixed(1)}M`
        }
        return amount.toLocaleString()
    }

    const renderGrowthIndicator = (rate: number) => {
        if (rate > 0) {
            return (
                <div className="flex items-center justify-end gap-0.5 text-green-600">
                    <TrendingUp size={14} />
                    <span className="text-xs font-medium">
                        +{rate.toFixed(1)}%
                    </span>
                </div>
            )
        } else if (rate < 0) {
            return (
                <div className="flex items-center justify-end gap-0.5 text-red-600">
                    <TrendingDown size={14} />
                    <span className="text-xs font-medium">
                        {rate.toFixed(1)}%
                    </span>
                </div>
            )
        }
        return (
            <div className="flex items-center justify-end gap-0.5 text-gray-400">
                <Minus size={14} />
                <span className="text-xs font-medium">-</span>
            </div>
        )
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null

        const item = payload[0].payload

        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                <p className="font-semibold text-gray-800 mb-2">
                    {item.product_group_name}
                </p>
                <div className="space-y-1 text-sm">
                    <p>
                        {t('total_services')} ({year}):{' '}
                        <span className="font-medium">
                            {item.total_services.toLocaleString()}
                        </span>
                    </p>
                    {showComparison && previousYearData && (
                        <p className="text-gray-500">
                            {t('total_services')} ({year - 1}):{' '}
                            <span className="font-medium">
                                {item.prev_total_services.toLocaleString()}
                            </span>
                        </p>
                    )}
                    <p
                        className={
                            item.new_services
                                ? 'text-green-600'
                                : 'text-gray-400'
                        }
                    >
                        {t('new_services')}:{' '}
                        <span className="font-medium">
                            {item.new_services
                                ? `+${item.new_services.toLocaleString()}`
                                : '-'}
                        </span>
                    </p>
                    <p
                        className={
                            item.cancelled_services
                                ? 'text-red-600'
                                : 'text-gray-400'
                        }
                    >
                        {t('cancelled_services')}:{' '}
                        <span className="font-medium">
                            {item.cancelled_services
                                ? `-${item.cancelled_services.toLocaleString()}`
                                : '-'}
                        </span>
                    </p>
                    <p className="text-purple-600">
                        {t('lost_revenue')}:{' '}
                        <span className="font-medium">
                            {formatCurrency(item.lost_revenue)}
                        </span>
                    </p>
                    <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                        <span>
                            {t('retention_rate')}:{' '}
                            {item.retention_rate.toFixed(1)}%
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    // Generate stripe patterns for each color
    const StripedPatterns = () => (
        <defs>
            {COLORS.map((color, index) => (
                <pattern
                    key={`stripe-${index}`}
                    id={`stripe-${index}`}
                    patternUnits="userSpaceOnUse"
                    width="8"
                    height="8"
                    patternTransform="rotate(45)"
                >
                    <rect width="4" height="8" fill={color} fillOpacity={0.4} />
                    <rect x="4" width="4" height="8" fill="white" />
                </pattern>
            ))}
        </defs>
    )

    if (allChartData.length === 0) {
        return (
            <div className="flex justify-center items-center h-[300px] text-gray-500 bg-white rounded-md">
                {t('no_data_available')}
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Product Group Selection */}
            <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4 flex-wrap">
                    <Typogaphy className="text-sm font-medium text-gray-700">
                        {t('reports.services.filter_product_groups')}:
                    </Typogaphy>
                    {/* Select All checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            className="w-4 h-4 rounded border-gray-300 text-gray-800 focus:ring-gray-500"
                        />
                        <span className="text-sm text-gray-700 font-medium">
                            {t('all')}
                        </span>
                    </label>
                    <div className="w-px h-5 bg-gray-300" />
                    {/* Individual product group checkboxes */}
                    {allChartData.map((item) => {
                        const isSelected = selectedGroups.has(
                            item.product_group_name
                        )
                        return (
                            <label
                                key={item.product_group_name}
                                className="flex items-center gap-2 cursor-pointer"
                                title={item.product_group_name}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    onChange={() =>
                                        toggleGroup(item.product_group_name)
                                    }
                                />
                                <Typogaphy
                                    className="text-sm"
                                    style={{ color: item.color }}
                                >
                                    {item.product_group_name}
                                </Typogaphy>
                            </label>
                        )
                    })}
                </div>
            </div>

            {/* Chart Section */}
            <div
                ref={chartRef}
                className="bg-white rounded-md p-4 shadow-sm border border-gray-100"
            >
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('services_by_product_group')}
                        </Typogaphy>
                        <Typogaphy className="text-sm text-gray-500">
                            {t('service_statistics_by_product_group')}
                        </Typogaphy>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex gap-4 text-right">
                            <div>
                                <Typogaphy className="text-xl font-bold text-blue-600">
                                    {totalServices.toLocaleString()}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-500">
                                    {t('total_services')}
                                </Typogaphy>
                            </div>
                            <div>
                                <Typogaphy className="text-xl font-bold text-green-600">
                                    {avgRetentionRate.toFixed(1)}%
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-500">
                                    {t('avg_retention_rate')}
                                </Typogaphy>
                            </div>
                            <div>
                                <Typogaphy className="text-xl font-bold text-red-600">
                                    {formatCurrency(totalLostRevenue)}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-500">
                                    {t('total_lost_revenue')}
                                </Typogaphy>
                            </div>
                        </div>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={exportToImage}
                            className="!min-w-0 !px-2"
                            title={t('download_image')}
                        >
                            <ImageDown size={16} />
                        </Button>
                    </div>
                </div>

                <ResponsiveContainer width="100%" height={450}>
                    <BarChart
                        data={chartData}
                        margin={{ left: 10, right: 20, bottom: 80 }}
                    >
                        <StripedPatterns />
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="shortName"
                            stroke="#9ca3af"
                            fontSize={10}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                        />
                        <YAxis stroke="#9ca3af" fontSize={11} />
                        <Tooltip content={<CustomTooltip />} />
                        {showComparison && previousYearData && (
                            <Legend
                                verticalAlign="top"
                                height={36}
                                content={({ payload }) => (
                                    <div className="flex justify-center gap-6 mb-2">
                                        {payload?.map((entry, index) => (
                                            <div
                                                key={`legend-${index}`}
                                                className="flex items-center gap-2"
                                            >
                                                {entry.dataKey ===
                                                'prev_total_services' ? (
                                                    // Striped pattern icon for previous year
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                    >
                                                        <defs>
                                                            <pattern
                                                                id="legend-stripe"
                                                                patternUnits="userSpaceOnUse"
                                                                width="4"
                                                                height="4"
                                                                patternTransform="rotate(45)"
                                                            >
                                                                <rect
                                                                    width="2"
                                                                    height="4"
                                                                    fill={
                                                                        SCHEMES
                                                                            .mono
                                                                            .gray[1]
                                                                    }
                                                                    fillOpacity={
                                                                        0.5
                                                                    }
                                                                />
                                                                <rect
                                                                    x="2"
                                                                    width="2"
                                                                    height="4"
                                                                    fill="white"
                                                                />
                                                            </pattern>
                                                        </defs>
                                                        <rect
                                                            width="14"
                                                            height="14"
                                                            x="1"
                                                            y="1"
                                                            rx="2"
                                                            fill="url(#legend-stripe)"
                                                            stroke={
                                                                SCHEMES.mono
                                                                    .gray[2]
                                                            }
                                                            strokeWidth="1"
                                                        />
                                                    </svg>
                                                ) : (
                                                    // Solid square for current year
                                                    <div
                                                        className="w-4 h-4 rounded"
                                                        style={{
                                                            backgroundColor:
                                                                SCHEMES.info[0],
                                                        }}
                                                    />
                                                )}
                                                <span className="text-sm text-gray-600">
                                                    {entry.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            />
                        )}

                        {/* Current year bars */}
                        <Bar
                            dataKey="total_services"
                            name={`${t('total_services')} ${year}`}
                            radius={[4, 4, 0, 0]}
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                />
                            ))}
                        </Bar>

                        {/* Previous year bars (striped) */}
                        {showComparison && previousYearData && (
                            <Bar
                                dataKey="prev_total_services"
                                name={`${t('total_services')} ${year - 1}`}
                                radius={[4, 4, 0, 0]}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-prev-${index}`}
                                        fill={`url(#stripe-${index % COLORS.length})`}
                                        stroke={entry.color}
                                        strokeWidth={1}
                                    />
                                ))}
                            </Bar>
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Service Breakdown Chart */}
            <div
                ref={breakdownChartRef}
                className="bg-white rounded-md p-4 shadow-sm border border-gray-100"
            >
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('reports.services.breakdown_by_product_group')}
                        </Typogaphy>
                        <Typogaphy className="text-sm text-gray-500">
                            {t('reports.services.breakdown_description')}
                        </Typogaphy>
                    </div>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                            if (!breakdownChartRef.current) return
                            const canvas = await html2canvas(
                                breakdownChartRef.current,
                                {
                                    backgroundColor: '#ffffff',
                                    scale: 2,
                                }
                            )
                            const link = document.createElement('a')
                            link.download = `product_group_breakdown_${fromDate}_${toDate}.png`
                            link.href = canvas.toDataURL('image/png')
                            link.click()
                        }}
                        className="!min-w-0 !px-2"
                        title={t('download_image')}
                    >
                        <ImageDown size={16} />
                    </Button>
                </div>

                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={chartData}
                        margin={{ left: 10, right: 20, bottom: 80 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="shortName"
                            stroke="#9ca3af"
                            fontSize={10}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                        />
                        <YAxis stroke="#9ca3af" fontSize={11} />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload || !payload.length)
                                    return null
                                const item = payload[0].payload
                                return (
                                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                                        <p className="font-semibold text-gray-800 mb-2">
                                            {item.product_group_name}
                                        </p>
                                        <div className="space-y-1 text-sm">
                                            <p className="text-blue-600">
                                                {t('reports.services.retained')}
                                                :{' '}
                                                <span className="font-medium">
                                                    {item.retained_services.toLocaleString()}
                                                </span>
                                            </p>
                                            <p className="text-green-600">
                                                {t('new_services')}:{' '}
                                                <span className="font-medium">
                                                    +
                                                    {item.new_services.toLocaleString()}
                                                </span>
                                            </p>
                                            <p className="text-red-600">
                                                {t('cancelled_services')}:{' '}
                                                <span className="font-medium">
                                                    -
                                                    {item.cancelled_services.toLocaleString()}
                                                </span>
                                            </p>
                                            <div className="border-t border-gray-100 pt-1 mt-1">
                                                <p className="text-gray-800 font-medium">
                                                    {t('total_services')}:{' '}
                                                    {item.total_services.toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }}
                        />
                        <Legend verticalAlign="top" height={36} />

                        {/* Retained services (stacked) */}
                        <Bar
                            dataKey="retained_services"
                            name={t('reports.services.retained')}
                            stackId="breakdown"
                            fill={SCHEMES.info[0]}
                            radius={[0, 0, 0, 0]}
                        />

                        {/* New services (stacked on top) */}
                        <Bar
                            dataKey="new_services"
                            name={t('new_services')}
                            stackId="breakdown"
                            fill={SCHEMES.success[0]}
                            radius={[4, 4, 0, 0]}
                        />

                        {/* Cancelled services (separate bar) */}
                        <Bar
                            dataKey="cancelled_services"
                            name={t('cancelled_services')}
                            fill={SCHEMES.error[0]}
                            radius={[4, 4, 0, 0]}
                        />
                    </BarChart>
                </ResponsiveContainer>

                {/* Analysis Section */}
                {analysis && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                            <Lightbulb size={16} className="text-amber-500" />
                            <span className="text-sm font-medium text-gray-700">
                                {t('reports.analysis.key_insights')}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="bg-blue-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">
                                    {t('reports.analysis.top_product_group')}
                                </p>
                                <p
                                    className="text-sm font-semibold text-blue-600 truncate"
                                    title={
                                        analysis.topProduct.product_group_name
                                    }
                                >
                                    {analysis.topProduct.product_group_name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {analysis.topProduct.total_services.toLocaleString()}{' '}
                                    {t('services')}
                                </p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">
                                    {t('reports.analysis.fastest_growing')}
                                </p>
                                <p
                                    className="text-sm font-semibold text-green-600 truncate"
                                    title={
                                        analysis.fastestGrowing
                                            .product_group_name
                                    }
                                >
                                    {analysis.fastestGrowing.product_group_name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {analysis.fastestGrowing.growth_rate >= 0
                                        ? '+'
                                        : ''}
                                    {analysis.fastestGrowing.growth_rate.toFixed(
                                        1
                                    )}
                                    %
                                </p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">
                                    {t('reports.analysis.best_retention')}
                                </p>
                                <p
                                    className="text-sm font-semibold text-purple-600 truncate"
                                    title={
                                        analysis.bestRetention
                                            .product_group_name
                                    }
                                >
                                    {analysis.bestRetention.product_group_name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {analysis.bestRetention.retention_rate.toFixed(
                                        1
                                    )}
                                    %
                                </p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">
                                    {t('reports.analysis.most_cancelled')}
                                </p>
                                <p
                                    className="text-sm font-semibold text-red-600 truncate"
                                    title={
                                        analysis.mostCancelled
                                            .product_group_name
                                    }
                                >
                                    {analysis.mostCancelled.product_group_name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    -
                                    {analysis.mostCancelled.cancelled_services.toLocaleString()}{' '}
                                    {t('services')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Table Section */}
            <div
                ref={tableRef}
                className="bg-white rounded-md p-4 shadow-sm border border-gray-100"
            >
                <div className="flex items-center justify-between mb-4">
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('detail_statistics')}
                    </Typogaphy>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToExcel}
                        className="!min-w-0 !px-2"
                        title={t('download_excel')}
                    >
                        <Download size={16} />
                    </Button>
                </div>

                <div className="overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                    {t('product_group')}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">
                                    {t('total')}
                                    {showComparison && previousYearData && (
                                        <span className="text-gray-400 font-normal">
                                            {' '}
                                            / {year - 1}
                                        </span>
                                    )}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-green-600">
                                    {t('new')}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-red-600">
                                    {t('cancelled')}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-purple-600">
                                    {t('lost_revenue')}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">
                                    {t('retention')}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">
                                    {t('growth')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {chartData.map((item, idx) => (
                                <tr
                                    key={item.product_group_name}
                                    className={
                                        idx % 2 === 0
                                            ? 'bg-white'
                                            : 'bg-gray-50'
                                    }
                                >
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="w-3 h-3 rounded-full flex-shrink-0"
                                                style={{
                                                    backgroundColor: item.color,
                                                }}
                                            />
                                            <span
                                                className="font-medium text-gray-900"
                                                title={item.product_group_name}
                                            >
                                                {item.product_group_name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700 font-medium">
                                        {item.total_services.toLocaleString()}
                                        {showComparison && previousYearData && (
                                            <span className="text-gray-400 font-normal text-xs ml-1">
                                                /{' '}
                                                {item.prev_total_services.toLocaleString()}
                                            </span>
                                        )}
                                    </td>
                                    <td
                                        className={`px-3 py-2 text-right ${item.new_services ? 'text-green-600' : 'text-gray-400'}`}
                                    >
                                        {item.new_services
                                            ? `+${item.new_services}`
                                            : '-'}
                                    </td>
                                    <td
                                        className={`px-3 py-2 text-right ${item.cancelled_services ? 'text-red-600' : 'text-gray-400'}`}
                                    >
                                        {item.cancelled_services
                                            ? `-${item.cancelled_services}`
                                            : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-right text-purple-600">
                                        {formatCurrency(item.lost_revenue)}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700">
                                        {item.retention_rate.toFixed(1)}%
                                    </td>
                                    <td className="px-3 py-2">
                                        {renderGrowthIndicator(
                                            item.growth_rate
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default ProductGroupReport
